import React, { useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, ScrollView, Dimensions, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { lightTheme, darkTheme } from '../../theme';
import { useColorScheme } from 'react-native';
import { BlurView } from 'expo-blur';

interface PWAInstallModalProps {
  visible: boolean;
  isIOS: boolean;
  onInstall: () => void;
  onDismiss: () => void;
  onDontShowAgain: () => void;
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

export function PWAInstallModal({
  visible,
  isIOS,
  onInstall,
  onDismiss,
  onDontShowAgain,
}: PWAInstallModalProps) {
  const { t } = useTranslation();
  const colorScheme = useColorScheme();
  const theme = colorScheme === 'dark' ? darkTheme : lightTheme;
  const isDark = colorScheme === 'dark';

  const benefits = [
    {
      icon: 'cloud-offline-outline',
      title: t('pwa.benefits.offline'),
      description: t('pwa.benefits.offlineDesc'),
    },
    {
      icon: 'rocket-outline',
      title: t('pwa.benefits.performance'),
      description: t('pwa.benefits.performanceDesc'),
    },
    {
      icon: 'notifications-outline',
      title: t('pwa.benefits.notifications'),
      description: t('pwa.benefits.notificationsDesc'),
    },
    {
      icon: 'camera-outline',
      title: t('pwa.benefits.camera'),
      description: t('pwa.benefits.cameraDesc'),
    },
  ];

  const renderIOSInstructions = () => (
    <View style={[styles.iosInstructions, { backgroundColor: theme.colors.surface }]}>
      <Text style={[styles.iosInstructionTitle, { color: theme.colors.text }]}>
        {t('pwa.ios.howTo')}
      </Text>
      <View style={styles.iosStep}>
        <View style={[styles.iosStepNumber, { backgroundColor: theme.colors.primary }]}>
          <Text style={styles.iosStepNumberText}>1</Text>
        </View>
        <Text style={[styles.iosStepText, { color: theme.colors.text }]}>
          {t('pwa.ios.step1')}
        </Text>
      </View>
      <View style={styles.iosStep}>
        <View style={[styles.iosStepNumber, { backgroundColor: theme.colors.primary }]}>
          <Text style={styles.iosStepNumberText}>2</Text>
        </View>
        <Text style={[styles.iosStepText, { color: theme.colors.text }]}>
          {t('pwa.ios.step2')}
        </Text>
      </View>
      <View style={styles.iosStep}>
        <View style={[styles.iosStepNumber, { backgroundColor: theme.colors.primary }]}>
          <Text style={styles.iosStepNumberText}>3</Text>
        </View>
        <Text style={[styles.iosStepText, { color: theme.colors.text }]}>
          {t('pwa.ios.step3')}
        </Text>
      </View>
    </View>
  );

  return (
    <Modal
      transparent
      visible={visible}
      animationType="fade"
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={onDismiss}
      >
        {Platform.OS === 'ios' && (
          <BlurView intensity={20} tint={isDark ? 'dark' : 'light'} style={StyleSheet.absoluteFillObject} />
        )}
        {Platform.OS !== 'ios' && (
          <View style={[StyleSheet.absoluteFillObject, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]} />
        )}
        
        <TouchableOpacity
          activeOpacity={1}
          style={[styles.modalContainer, { backgroundColor: theme.colors.surface }]}
          onPress={(e) => e.stopPropagation()}
        >
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={styles.modalContent}>
      <View style={[styles.header, { backgroundColor: theme.colors.primaryLight + '20' }]}>
        <Ionicons name="phone-portrait-outline" size={48} color={theme.colors.primary} />
      </View>
      
      <Text style={[styles.title, { color: theme.colors.text }]}>
        {t('pwa.modalTitle')}
      </Text>
      
      <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
        {t('pwa.modalSubtitle')}
      </Text>

      <View style={styles.benefitsContainer}>
        {benefits.map((benefit, index) => (
          <View key={index} style={styles.benefitRow}>
            <View style={[styles.benefitIcon, { backgroundColor: theme.colors.primary + '15' }]}>
              <Ionicons name={benefit.icon as any} size={20} color={theme.colors.primary} />
            </View>
            <View style={styles.benefitText}>
              <Text style={[styles.benefitTitle, { color: theme.colors.text }]}>
                {benefit.title}
              </Text>
              <Text style={[styles.benefitDescription, { color: theme.colors.textSecondary }]}>
                {benefit.description}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {isIOS && renderIOSInstructions()}

              <TouchableOpacity
                onPress={onDontShowAgain}
                style={styles.dontShowAgain}
              >
                <Text style={[styles.dontShowAgainText, { color: theme.colors.textMuted }]}>
                  {t('pwa.dontShowAgain')}
                </Text>
              </TouchableOpacity>

              <View style={styles.buttonContainer}>
                {isIOS ? (
                  <TouchableOpacity
                    style={[styles.button, styles.primaryButton, { backgroundColor: theme.colors.primary }]}
                    onPress={onDismiss}
                  >
                    <Text style={styles.primaryButtonText}>{t('common.gotIt')}</Text>
                  </TouchableOpacity>
                ) : (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.secondaryButton, { borderColor: theme.colors.border }]}
                      onPress={onDismiss}
                    >
                      <Text style={[styles.secondaryButtonText, { color: theme.colors.text }]}>
                        {t('pwa.maybeLater')}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.button, styles.primaryButton, { backgroundColor: theme.colors.primary }]}
                      onPress={onInstall}
                    >
                      <Text style={styles.primaryButtonText}>{t('pwa.installNow')}</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </View>
          </ScrollView>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 20,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  modalContent: {
    width: '100%',
    padding: 24,
  },
  header: {
    alignItems: 'center',
    paddingVertical: 20,
    marginBottom: 16,
    borderRadius: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
    paddingHorizontal: 8,
  },
  benefitsContainer: {
    marginBottom: 24,
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  benefitIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  benefitText: {
    flex: 1,
  },
  benefitTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  benefitDescription: {
    fontSize: 14,
    lineHeight: 18,
  },
  iosInstructions: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  iosInstructionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  iosStep: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  iosStepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  iosStepNumberText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  iosStepText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  dontShowAgain: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  dontShowAgainText: {
    fontSize: 13,
    textDecorationLine: 'underline',
  },
  buttonContainer: {
    flexDirection: 'row',
    marginTop: 24,
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButton: {
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  secondaryButton: {
    borderWidth: 1,
    backgroundColor: 'transparent',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
});